import { api, IS_PROD } from '../config/api';
import { mockOverview, mockVisitors, mockSettings } from './embedMock';

// ---- Fit-check embed insights (embed.outfino.io) ----
//
// The brand-facing side of the fit-check button's analytics. Contract
// (Outfino/api feature/embed-analytics, brand-scoped):
//
//   GET /brands/:id/embed/overview?from&to                     viewAnalytics
//     → { totals: { impressions, clicks, signups, logins, matchViews,
//                   uploadSessions, photosUploaded, uniqueVisitors,
//                   addToCarts, addToCartsMatched, purchases, purchasesMatched,
//                   revenueMatched: [{ currency, amount }], follows },
//         rates: {…}, daily: [{ date, impressions, clicks, signups, logins,
//                   matchViews, photosUploaded, addToCarts, purchases, follows }],
//         topProducts: [{ shopifyProductId, handle, title, imageUrl,
//                   impressions, clicks, signups, matchViews, photosUploaded,
//                   avgCompatibility, addToCarts, addToCartsMatched, purchases,
//                   purchasesMatched, revenueMatched: [{ currency, amount }],
//                   follows }] }
//   GET /brands/:id/embed/visitors?from&to&productId&event&cursor viewAnalytics
//     → { items: [{ id, user: { id, username, avatarUrl },
//                   event: 'signup'|'login'|'match_view'|'upload'
//                          |'add_to_cart'|'purchase'|'follow',
//                   product: { shopifyProductId, handle, title, imageUrl },
//                   photosUploaded, compatibility, occurredAt,
//                   matched?, quantity?, orderValue?: { amount, currency } }],
//         nextCursor }   — purchases never carry the order id or customer data
//   GET /brands/:id/embed/settings                               editBrand
//     → { key, allowedDomains, status: 'configured'|'not_configured',
//         lastSeenAt, seenDomains: [{ domain, lastSeenAt, impressions7d }],
//         tracking: { pixelStatus: 'configured'|'not_configured',
//                     lastAddToCartAt, lastPurchaseAt,
//                     platformsSeen: ['shopify'|'woocommerce'|'custom'] } }
//
// "Matched" (carts, purchases, revenue): the same visitor viewed the product
// matching for that product within the 30 days before.
//
// `from` / `to` are local calendar days, YYYY-MM-DD, both inclusive.
//
// TEST DATA (api/embedMock.js) stands in until those endpoints are deployed:
//   • REACT_APP_EMBED_MOCK=1 → always the test data, in any build;
//   • otherwise, outside production builds, when the endpoint answers 404.
// A production build never shows it unless the flag is set. Every loader
// resolves to { data, mock } so the page can label test data as such.

const FORCE_MOCK = process.env.REACT_APP_EMBED_MOCK === '1';

export function embedMockAllowed({ force = FORCE_MOCK, prod = IS_PROD } = {}) {
  return force || !prod;
}

export async function withEmbedMock(load, mock, { force = FORCE_MOCK, prod = IS_PROD } = {}) {
  if (force) return { data: await mock(), mock: true };
  try {
    return { data: await load(), mock: false };
  } catch (err) {
    if (err?.status === 404 && embedMockAllowed({ force, prod })) return { data: await mock(), mock: true };
    throw err;
  }
}

// A beat of latency for the test data, so loading states look like they will.
export const later = (fn) => () => new Promise((resolve) => setTimeout(() => resolve(fn()), 220));

export function query(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function fetchEmbedOverview(brandId, { from, to } = {}) {
  return withEmbedMock(
    () => api(`/brands/${brandId}/embed/overview${query({ from, to })}`),
    later(() => mockOverview(brandId, { from, to }))
  );
}

export function fetchEmbedVisitors(brandId, { from, to, productId, event, cursor } = {}) {
  return withEmbedMock(
    () => api(`/brands/${brandId}/embed/visitors${query({ from, to, productId, event, cursor })}`),
    later(() => mockVisitors(brandId, { from, to, productId, event, cursor }))
  );
}

// `mockStatus` only steers the test data (to see the "not set up" state).
export function fetchEmbedSettings(brandId, { mockStatus } = {}) {
  return withEmbedMock(
    () => api(`/brands/${brandId}/embed/settings`),
    later(() => mockSettings(brandId, { status: mockStatus }))
  );
}
